import React, { useState } from "react";
import toast from "react-hot-toast";

import { AxiosError } from "axios";
import { useOktaAuth } from "@okta/okta-react";

import { CheckmarkFilled, Eye16Filled, EyeOff16Filled } from "@fluentui/react-icons";
import { Button, Caption1, Field, Input, Spinner, Text } from "@fluentui/react-components";

import { useResetPasswordStyles } from "./styles";
import { useSettingsStyles } from "../styles";

import log from "@/utils/logger";
import { updatePassword } from "@/services/auth-service";

const PASSWORD_CONDITIONS = [
  { key: "min-8-chars", label: "Your password must be at least 8 characters long." },
  { key: "one-lower", label: "It must include at least one lowercase letter." },
  { key: "one-upper", label: "It must include at least one uppercase letter." },
  { key: "one-number", label: "It must include at least one number." },
  { key: "one-special", label: "It must include at least one special character." },
];

interface UpdatePasswordProps extends React.ComponentPropsWithoutRef<"form"> {}

export const UpdatePassword = (props: UpdatePasswordProps) => {
  const settingsStyles = useSettingsStyles();
  const styles = useResetPasswordStyles();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>(null);
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [repeatPasswordError, setRepeatPasswordError] = useState<string>(null);

  const { authState } = useOktaAuth();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (password !== repeatPassword) {
      setRepeatPasswordError("Your passwords do not match.");
      return;
    }

    if (!currentPassword.trim()) {
      setError("Current password is required.");
      return;
    }

    setLoading(true);
    setError(null);
    setRepeatPasswordError(null);

    try {
      await updatePassword(currentPassword, password);
      setError(null);
      setCurrentPassword("");
      setPassword("");
      setRepeatPassword("");
      toast.success("Password updated successfully");
    } catch (error) {
      log.error("Update password error", undefined, error);
      if (error instanceof AxiosError) {
        setError(error.response?.data || error.message || "Failed to update password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="off" {...props}>
      <div className={settingsStyles.fieldContainer}>
        <Field label="Update Password" />
        <div className={styles.passwordsContainer}>
          <Input
            id="current-password"
            data-lpignore="true"
            placeholder="Enter your current password"
            disabled={authState.isAuthenticated}
            type={currentPasswordVisible ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            contentAfter={
              <Button
                disabled={authState.isAuthenticated}
                icon={currentPasswordVisible ? <Eye16Filled /> : <EyeOff16Filled />}
                onClick={() => setCurrentPasswordVisible((prev) => !prev)}
                appearance="transparent"
              />
            }
            required
            className={settingsStyles.input}
          />
          <Input
            id="password"
            data-lpignore="true"
            placeholder="Enter your new password"
            disabled={authState.isAuthenticated}
            type={passwordVisible ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            contentAfter={
              <Button
                disabled={authState.isAuthenticated}
                icon={passwordVisible ? <Eye16Filled /> : <EyeOff16Filled />}
                onClick={() => setPasswordVisible((prev) => !prev)}
                appearance="transparent"
              />
            }
            required
            className={settingsStyles.input}
          />
          <Input
            id="repeat-password"
            data-lpignore="true"
            placeholder="Repeat your new password"
            disabled={authState.isAuthenticated}
            type={repeatPasswordVisible ? "text" : "password"}
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            contentAfter={
              <div className={styles.repeatPasswordIcons}>
                <Button
                  disabled={authState.isAuthenticated}
                  icon={repeatPasswordVisible ? <Eye16Filled /> : <EyeOff16Filled />}
                  onClick={() => setRepeatPasswordVisible((prev) => !prev)}
                  appearance="transparent"
                />
                {!!password && !!repeatPassword && password === repeatPassword && <CheckmarkFilled fontSize={16} />}
              </div>
            }
            required
            className={settingsStyles.input}
          />
        </div>
        <ul className={styles.passwordInfoContainer}>
          {PASSWORD_CONDITIONS.map((condition) => (
            <li key={condition.key}>
              <Caption1>{condition.label}</Caption1>
            </li>
          ))}
        </ul>
      </div>
      {repeatPasswordError && (
        <div>
          <Text className={settingsStyles.errorText}>{repeatPasswordError}</Text>
        </div>
      )}
      {error && (
        <div>
          <Text className={settingsStyles.errorText}>{error}</Text>
        </div>
      )}
      <Button type="submit" appearance="primary" disabled={loading || authState.isAuthenticated}>
        {loading ? <Spinner size="extra-tiny" label="Saving..." /> : "Save"}
      </Button>
    </form>
  );
};
